import React from 'react';
import './Pages/PagesCss/LegalInformation.css';

const ContentPolicy = () => {
  return (
    <div className="privacy-policy">
      {/* Header Section */}
      <section className="policy-header">
        <div className="container">
          <div className="header-content">
            <h1>Content Policy</h1>
            <p className="last-updated">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <p className="header-description">
              This Content Policy outlines the acceptable use of Font-Max's text processing tools and services. 
              By using our platform, you agree to comply with these guidelines and ensure your content meets our standards.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="policy-content">
        <div className="container-one">
          <div className="policy-grid">
            {/* Table of Contents */}
            <aside className="table-of-contents">
              <h3>Table of Contents</h3>
              <div>
                <ul>
                  <li><a href="#introduction">Introduction</a></li>
                  <li><a href="#acceptable-content">Acceptable Content</a></li>
                  <li><a href="#prohibited-content">Prohibited Content</a></li>
                  <li><a href="#copyright-ip">Copyright & Intellectual Property</a></li>
                  <li><a href="#user-responsibilities">User Responsibilities</a></li>
                  <li><a href="#content-monitoring">Content Monitoring</a></li>
                  <li><a href="#violations-consequences">Violations & Consequences</a></li>
                  <li><a href="#reporting-abuse">Reporting Abuse</a></li>
                  <li><a href="#policy-updates">Policy Updates</a></li>
                  <li><a href="#contact-us">Contact Us</a></li>
                </ul>
              </div>
            </aside>

            {/* Policy Sections */}
            <div className="policy-sections">
              <section id="introduction" className="policy-section">
                <h2>1. Introduction</h2>
                <div className="section-content">
                  <p>
                    This Content Policy applies to all websites and subdomains operated by D-Tech Studios for Font-Max, including <a href="https://font-max.com" target="_blank" rel="noopener noreferrer">https://font-max.com</a>.
                    As a provider of text processing tools, we are committed to maintaining a safe and 
                    respectful environment for all users.
                  </p>
                  <p>
                    While we primarily process text locally and do not permanently store user content, 
                    we reserve the right to monitor and restrict content that violates this policy.
                  </p>
                </div>
              </section>

              <section id="acceptable-content" className="policy-section">
                <h2>2. Acceptable Content</h2>
                <div className="section-content">
                  <p>Font-Max is designed for legitimate text processing needs. Appropriate uses include:</p>
                  
                  <div className="acceptable-uses">
                    <h3>2.1 Personal & Educational Use</h3>
                    <ul>
                      <li>Academic writing and research content</li>
                      <li>Personal documents and communications</li>
                      <li>Creative writing and artistic projects</li>
                      <li>Educational materials and study aids</li>
                    </ul>

                    <h3>2.2 Professional & Business Use</h3>
                    <ul>
                      <li>Business documents and reports</li>
                      <li>Marketing materials and social media content</li>
                      <li>Code formatting and technical documentation</li>
                      <li>Data analysis and processing</li>
                    </ul>

                    <h3>2.3 Creative Use</h3>
                    <ul>
                      <li>Font generation for design projects</li>
                      <li>Symbol and emoji creation for communications</li>
                      <li>Text transformation for artistic purposes</li>
                      <li>Content formatting for web development</li>
                    </ul>
                  </div>

                  <div className="note-box">
                    <strong>Note:</strong> All content must comply with applicable laws and respect intellectual property rights.
                  </div>
                </div>
              </section>

              <section id="prohibited-content" className="policy-section">
                <h2>3. Prohibited Content</h2>
                <div className="section-content">
                  <p>The following types of content are strictly prohibited on Font-Max:</p>
                  
                  <div className="prohibited-categories">
                    <h3>3.1 Illegal Content</h3>
                    <ul>
                      <li>Content promoting or facilitating illegal activities</li>
                      <li>Threats, harassment, or incitement to violence</li>
                      <li>Fraudulent or deceptive materials</li>
                      <li>Content violating export control laws</li>
                    </ul>

                    <h3>3.2 Harmful & Malicious Content</h3>
                    <ul>
                      <li>Malware, viruses, or malicious code</li>
                      <li>Phishing attempts or security threats</li>
                      <li>Spam, unsolicited commercial content</li>
                      <li>Content designed to disrupt services</li>
                      <li>Automated scraping or bulk processing without permission</li>
                    </ul>

                    <h3>3.3 Offensive & Abusive Content</h3>
                    <ul>
                      <li>Hate speech targeting race, ethnicity, religion, gender, or sexual orientation</li>
                      <li>Bullying, intimidation, or harassment</li>
                      <li>Graphic violence or explicit content</li>
                      <li>Defamatory or libelous statements</li>
                    </ul>

                    <h3>3.4 Intellectual Property Violations</h3>
                    <ul>
                      <li>Copyrighted material without authorization</li>
                      <li>Trademark infringement</li>
                      <li>Trade secrets or confidential information</li>
                      <li>Academic dishonesty or plagiarism</li>
                    </ul>

                    <h3>3.5 System Abuse</h3>
                    <ul>
                      <li>Excessive requests that degrade service performance</li>
                      <li>Automated tools that bypass rate limits</li>
                      <li>Attempts to reverse engineer or compromise our systems</li>
                      <li>Content designed to exploit security vulnerabilities</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section id="copyright-ip" className="policy-section">
                <h2>4. Copyright & Intellectual Property</h2>
                <div className="section-content">
                  <h3>4.1 Respect for Intellectual Property</h3>
                  <p>
                    Users must ensure they have appropriate rights to all content processed through our tools. 
                    This includes:
                  </p>
                  <ul>
                    <li>Obtaining proper licenses for copyrighted material</li>
                    <li>Respecting trademark protections</li>
                    <li>Following open source and creative commons licenses</li>
                    <li>Avoiding plagiarism and academic dishonesty</li>
                  </ul>

                  <h3>4.2 Generated Content Rights</h3>
                  <p>
                    Font styles, symbols, and other generated elements may incorporate third-party intellectual property. 
                    Users are responsible for:
                  </p>
                  <ul>
                    <li>Verifying licensing requirements for generated content</li>
                    <li>Ensuring commercial use complies with applicable licenses</li>
                    <li>Attributing sources when required</li>
                  </ul>

                  <h3>4.3 DMCA Compliance</h3>
                  <p>
                    Font-Max respects intellectual property rights and complies with the Digital Millennium 
                    Copyright Act (DMCA). Copyright owners may submit takedown notices for infringing content.
                  </p>
                </div>
              </section>

              <section id="user-responsibilities" className="policy-section">
                <h2>5. User Responsibilities</h2>
                <div className="section-content">
                  <h3>5.1 Content Screening</h3>
                  <p>
                    Users are solely responsible for screening their content to ensure it complies with this policy 
                    and applicable laws. This includes:
                  </p>
                  <ul>
                    <li>Reviewing text before and after processing</li>
                    <li>Verifying rights to use third-party content</li>
                    <li>Ensuring content doesn't violate privacy rights</li>
                    <li>Checking for accidental inclusion of sensitive information</li>
                  </ul>

                  <h3>5.2 Legal Compliance</h3>
                  <p>
                    Users must ensure their use of Font-Max complies with all applicable local, state, national, 
                    and international laws, including:
                  </p>
                  <ul>
                    <li>Data protection and privacy regulations</li>
                    <li>Intellectual property laws</li>
                    <li>Export control regulations</li>
                    <li>Industry-specific compliance requirements</li>
                  </ul>

                  <h3>5.3 Ethical Use</h3>
                  <p>
                    We expect users to employ our tools ethically and responsibly, considering the potential 
                    impact of their generated content on others.
                  </p>
                </div>
              </section>

              <section id="content-monitoring" className="policy-section">
                <h2>6. Content Monitoring & Enforcement</h2>
                <div className="section-content">
                  <h3>6.1 Monitoring Approach</h3>
                  <p>
                    While we respect user privacy and primarily process content locally, we may monitor for:
                  </p>
                  <ul>
                    <li>Patterns of abusive behavior or system misuse</li>
                    <li>Automated tools violating rate limits</li>
                    <li>Content reported by other users</li>
                    <li>Security threats and malicious activity</li>
                  </ul>

                  <h3>6.2 Automated Detection</h3>
                  <p>
                    We may employ automated systems to detect:
                  </p>
                  <ul>
                    <li>Spam and bulk processing patterns</li>
                    <li>Known malicious content patterns</li>
                    <li>System abuse and rate limit violations</li>
                    <li>Security threats and exploit attempts</li>
                  </ul>

                  <h3>6.3 Investigation Rights</h3>
                  <p>
                    We reserve the right to investigate any suspected policy violations, which may include:
                  </p>
                  <ul>
                    <li>Reviewing usage patterns and logs</li>
                    <li>Examining reported content</li>
                    <li>Cooperating with law enforcement when required</li>
                    <li>Temporary service restrictions during investigations</li>
                  </ul>
                </div>
              </section>

              <section id="violations-consequences" className="policy-section">
                <h2>7. Violations & Consequences</h2>
                <div className="section-content">
                  <h3>7.1 Actions We May Take</h3>
                  <p>In response to policy violations, we may take one or more of the following actions:</p>
                  
                  <div className="consequences-grid">
                    <div className="consequence-item">
                      <h4>Warnings</h4>
                      <p>Formal notification of violation with requirement to cease</p>
                    </div>
                    <div className="consequence-item">
                      <h4>Temporary Suspension</h4>
                      <p>Temporary restriction of service access</p>
                    </div>
                    <div className="consequence-item">
                      <h4>Permanent Ban</h4>
                      <p>Permanent termination of access to our services</p>
                    </div>
                    <div className="consequence-item">
                      <h4>Content Removal</h4>
                      <p>Removal of violating content from our systems</p>
                    </div>
                    <div className="consequence-item">
                      <h4>Legal Action</h4>
                      <p>Pursuit of legal remedies for serious violations</p>
                    </div>
                    <div className="consequence-item">
                      <h4>Law Enforcement</h4>
                      <p>Reporting to appropriate authorities for illegal activities</p>
                    </div>
                  </div>

                  <h3>7.2 Factors Considered</h3>
                  <p>When determining appropriate consequences, we consider:</p>
                  <ul>
                    <li>Severity and nature of the violation</li>
                    <li>User's history and intent</li>
                    <li>Impact on other users and our services</li>
                    <li>Legal requirements and obligations</li>
                  </ul>
                </div>
              </section>

              <section id="reporting-abuse" className="policy-section">
                <h2>8. Reporting Abuse</h2>
                <div className="section-content">
                  <h3>8.1 How to Report</h3>
                  <p>
                    If you encounter content that violates this policy, please report it to us immediately at:
                  </p>
                  <div className="reporting-info">
                    <div className="contact-item">
                      <strong>Email:</strong> support@font-max.com
                    </div>
                    <div className="contact-item">
                      <strong>Subject Line:</strong> "Content Policy Violation Report"
                    </div>
                  </div>

                  <h3>8.2 Required Information</h3>
                  <p>When reporting violations, please include:</p>
                  <ul>
                    <li>Specific description of the violating content</li>
                    <li>URL or tool where the violation occurred</li>
                    <li>Date and time of the incident</li>
                    <li>Any relevant screenshots or evidence</li>
                    <li>Your contact information for follow-up</li>
                  </ul>

                  <h3>8.3 DMCA Reports</h3>
                  <p>
                    For copyright infringement reports, please include a complete DMCA takedown notice 
                    with all required elements as specified by law.
                  </p>

                  <div className="note-box">
                    <strong>Important:</strong> We investigate all reports promptly and will take appropriate 
                    action based on our investigation findings.
                  </div>
                </div>
              </section>

              <section id="policy-updates" className="policy-section">
                <h2>9. Policy Updates</h2>
                <div className="section-content">
                  <p>
                    We may update this Content Policy from time to time to reflect changes in our services, 
                    legal requirements, or industry standards. Significant changes will be communicated through:
                  </p>
                  <ul>
                    <li>Notifications on our website</li>
                    <li>Updated "Last updated" date on this page</li>
                    <li>Email notifications for registered users (if applicable)</li>
                  </ul>
                  <p>
                    Continued use of Font-Max services after policy updates constitutes acceptance of the 
                    revised terms.
                  </p>
                </div>
              </section>

              <section id="contact-us" className="policy-section">
                <h2>10. Contact Us</h2>
                <div className="section-content">
                  <p>If you have questions about this Content Policy or need to report violations, contact us:</p>
                  <div className="contact-info">
                    <div className="contact-item">
                      <strong>Content Policy Questions:</strong> support@font-max.com
                    </div>
                    <div className="contact-item">
                      <strong>Address:</strong> 2568 Madison Ave, League City, TX 77573
                    </div>
                    <div className="contact-item">
                      <strong>Website:</strong> <a href="https://font-max.com">https://font-max.com</a>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="policy-actions">
        <div className="container">
          <div className="actions-content">
            <h3>Need to Report Content?</h3>
            <p>If you encounter content that violates our policy or have questions about acceptable use, we're here to help.</p>
            <div className="action-buttons">
              <a href="/" className="btn btn-primary">Checkout Our Tools</a>
              <a href="/Contact" className="btn btn-secondary">Contact Support</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContentPolicy;